import React, { useState, useRef, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import "../styles/Createque.css";
import { FaBell, FaUserCircle } from "react-icons/fa";
import TiptapEditor from "../components/TiptapEditor";
import axios from 'axios';
import { toast } from 'react-toastify';

const Createque = () => {
  const [activeItem, setActiveItem] = useState("/questions/createque");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // API-driven lists
  const [boards, setBoards] = useState([]);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);

  // Selected values
  const [board, setBoard] = useState("");
  const [grade, setStandard] = useState(""); // UI field; will send as grade
  const [subject, setSubject] = useState("");
  const [chapter, setChapter] = useState("");
  const [difficultylevel, setDifficultylevel] = useState("");
  const [difficulties, setDifficulties] = useState([]);

  // Question state
  const [questionType, setQuestionType] = useState("M.C.Q");
  const [questionTitle, setQuestionTitle] = useState("");
  const [options, setOptions] = useState(["", "", "", ""]);
  const [correctAnswer, setCorrectAnswer] = useState(null);
  const [answer, setAnswerText] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Misc
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [dashboardData, setDashboardData] = useState({
    totalBoard: 0,
    totalGrade: 0,
    totalSubjects: 0,
    totalPapers: 0,
    totalQuestions: 0,
  });

  const [user, setUser] = useState({
    id: null,
    name: '',
    email: '',
    password: '',
  });
  const token = localStorage.getItem("token");

  // ----- helpers -----
  const toggleDropdown = () => setShowDropdown((p) => !p);
  const clearError = (field) => setErrors((prev) => ({ ...prev, [field]: "" }));

  const getArrayFromResponse = (data, key) => {
    // Accept: [] OR { [key]: [] } OR { data: [] }
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data[key])) return data[key];
    if (data && Array.isArray(data.data)) return data.data;
    return [];
  };

  const labelOf = (item, type) =>
    item?.name ||
    item?.[`${type}_name`] ||
    item?.title ||
    item?.label ||
    item?.chapter_name ||
    `#${item?.id}`;

  // 1. Fetch Boards on mount
  useEffect(() => {
    const fetchBoards = async () => {
      try {
        const res = await fetch("https://digital.tifleducation.com/admin/public/api/getBoards", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        setBoards(json.data || json.boards || []);
      } catch (err) {
        console.error("Error fetching boards:", err);
        setBoards([]);
      }
    };

    fetchBoards();
  }, [token]);

  // 2. Fetch Grades when Board selected
  useEffect(() => {
    if (!board) {
      setGrades([]);
      setSubjects([]);
      setChapters([]);
      return;
    }

    const fetchGrades = async () => {
      try {
        const res = await fetch(
          `https://digital.tifleducation.com/admin/public/api/getGrades?board_id=${board}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const json = await res.json();
        setGrades(json.data || json.grades || []);
        setStandard(""); // reset grade selection
        setSubjects([]);
        setChapters([]);
      } catch (err) {
        console.error("Error fetching grades:", err);
        setGrades([]);
      }
    };

    fetchGrades();
  }, [board, token]);

  // 3. Fetch Subjects when Board + Grade selected
  useEffect(() => {
    if (!board || !grade) {
      setSubjects([]);
      setChapters([]);
      return;
    }

    const fetchSubjects = async () => {
      try {
        const res = await fetch(
          `https://digital.tifleducation.com/admin/public/api/getSubjects?board_id=${board}&grade_id=${grade}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const json = await res.json();
        setSubjects(json.data || json.subjects || []);
        setSubject(""); // reset subject selection
        setChapters([]);
      } catch (err) {
        console.error("Error fetching subjects:", err);
        setSubjects([]);
      }
    };

    fetchSubjects();
  }, [board, grade, token]);

  // Fetch dashboard & user data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get("https://digital.tifleducation.com/admin/public/api/getDashboard", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data) {
          setDashboardData({
            totalBoard: res.data.totalBoards || 0,
            totalGrade: res.data.totalGrades || 0,
            totalSubjects: res.data.totalSubjects || 0,
            totalPapers: res.data.totalPapers || 0,
            totalQuestions: res.data.totalQuestions || 0,
          });
          if (res.data.user) {
            setUser({
              id: res.data.user.id,
              name: res.data.user.name || '',
              email: res.data.user.email || '',
              password: '', // empty for editing
            });
          }
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error.response?.data || error);
      }
    };

    fetchDashboardData();
  }, []);

  // Handle save changes
  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "https://digital.tifleducation.com/admin/public/api/update-profile",
        {
          name: user.name,
          password: user.password,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success("Profile updated successfully!");

      setIsModalOpen(false);
      setUser({ ...user, password: '' }); // reset password field
    } catch (error) {
      console.error("Error updating profile:", error.response?.data || error);

      toast.error("Failed to update profile");

    }
  };


  // 4. Fetch Chapters when Board + Grade + Subject selected
  useEffect(() => {
    if (!board || !grade || !subject) {
      setChapters([]);
      return;
    }

    const fetchChapters = async () => {
      try {
        const res = await fetch(
          `https://digital.tifleducation.com/admin/public/api/chapters?board_id=${board}&grade_id=${grade}&subject_id=${subject}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const json = await res.json();
        setChapters(json.data || json.chapters || []);
        setChapter("");
      } catch (err) {
        console.error("Error fetching chapters:", err);
        setChapters([]);
      }
    };

    fetchChapters();
  }, [board, grade, subject, token]);

  useEffect(() => {
    const fetchDifficulties = async () => {
      try {
        const res = await axios.get(
          "https://digital.tifleducation.com/admin/public/api/difficulties",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        // Handle possible Laravel formats
        if (Array.isArray(res.data)) {
          setDifficulties(res.data);
        } else if (Array.isArray(res.data.data)) {
          setDifficulties(res.data.data);
        } else {
          console.warn("Unexpected difficulty response:", res.data);
          toast.info("No difficulty levels found");
        }
      } catch (error) {
        console.error("Error fetching difficulties:", error.response?.data || error);
        toast.error("Failed to load difficulty levels");
      }
    };

    fetchDifficulties();
  }, [token]);

  // ----- editor / input handlers -----
  const handleOptionChange = (value, index) => {
    const updated = [...options];
    updated[index] = value;
    setOptions(updated);
    clearError("options");
  };

  // ----- static types from your HTML snippet -----
  const questionTypes = [
    { label: "M.C.Q", value: "M.C.Q" },
    { label: "True-False", value: "True-False" },
    { label: "Fill in the blanks", value: "Fill in the blanks" },
    { label: "Very Short Question Answer", value: "Very Short Question Answer" },
    { label: "Short Question Answer", value: "Short Question Answer" },
    { label: "Long Question Answer", value: "Long Question Answer" },
    { label: "Diagram-Based/Image-Based Questions", value: "Diagram-Based_Image-Based Questions" },
    { label: "Matching Types", value: "Matching Types" },
  ];

  // ----- validation -----
  const validateForm = () => {
    const newErrors = {};
    if (!board) newErrors.board = "Board is required.";
    if (!grade) newErrors.grade = "Grade is required.";
    if (!subject) newErrors.subject = "Subject is required.";
    if (!chapter) newErrors.chapter = "Chapter is required.";
    if (!difficultylevel) newErrors.difficultylevel = "Difficulty level is required.";
    if (!questionType) newErrors.questionType = "Select question type.";
    if (questionType !== "Matching Types" && !questionTitle.trim()) {
      newErrors.questionTitle = "Question title is required.";
    }

    if (questionType === "M.C.Q") {
      if (options.some((opt) => !opt.trim())) newErrors.options = "All options must be filled.";
      if (correctAnswer === null) newErrors.correctAnswer = "Select the correct answer.";
    }

    if (
      ["Fill in the blanks", "Very Short Question Answer", "Short Question Answer", "Long Question Answer", "Diagram-Based_Image-Based Questions"].includes(
        questionType
      ) && !answer.trim()
    ) {
      newErrors.answer = "Answer is required.";
    }

    if (questionType === "True-False" && !answer) {
      newErrors.answer = "Please select True or False.";
    }

    if (questionType === "Matching Types") {
      if (!options[0].trim() || !options[1].trim()) newErrors.options = "Both left and right columns must be filled.";
      if (!answer.trim()) newErrors.answer = "Answer is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ----- save -----
  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      board,
      grade,
      subject,
      chapter,
      difficulty_level: difficultylevel,
      question_type: questionType,
      question_title: questionTitle,
    };

    if (questionType === "M.C.Q") {
      payload.option_1 = options[0];
      payload.option_2 = options[1];
      payload.option_3 = options[2] || null;
      payload.option_4 = options[3] || null;
      payload.correct_answer = String(correctAnswer + 1); // Laravel expects 1-4, not 0-3
    }

    if (questionType === "True-False") {
      payload.answer = answer; // "True" or "False"
    }

    if (["Fill in the blanks", "Very Short Question Answer", "Short Question Answer", "Long Question Answer", "Diagram-Based_Image-Based Questions"].includes(questionType)) {
      payload.answer = answer;
    }

    if (questionType === "Matching Types") {
      payload.match_left = options[0];
      payload.match_right = options[1];
      payload.answer = answer;
    }
    try {
      const res = await fetch("https://digital.tifleducation.com/admin/public/api/question", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(`Error: ${res.status}`);
      await res.json();
      toast.success("Question saved successfully!");
      setTimeout(() =>  1500);
     
      window.location.href = "/questions/myquestions";
    } catch (err) {
      console.error("❌ Failed to save question:", err);
      toast.error("Failed to save question. Try again..");
      
    }
  };

  // ----- UI -----
  return (
    <div className="dashboard-container">
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
      />

      <main className="main-content">
        {/* Header */}
        <div className="createque-header">
          <div className="createque-welcome-text">
            <a href="/dashboard" className="back-link">
              <i className="fa fa-arrow-left" aria-hidden="true" />
            </a>
            <span className="createque-welcome-text">Create Questions</span>
          </div>
          <div className="header-icons" ref={dropdownRef}>
            <FaBell className="icon" />
            <div className="user-dropdown-wrapper">
              <FaUserCircle className="icon" onClick={toggleDropdown} />
              {showDropdown && (
                <div className="user-dropdown">
                  <div className='edituser' onClick={() => setIsModalOpen(true)}>
                    <FaUserCircle className="ditusericon" />
                    <span>Edit Profile</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Dropdowns */}
        <div className="dropdown-row">
          {/* Board */}
          <div className="dropdown-group">
            <label>Board <span style={{ color: "red" }}>*</span></label>
            <select
              value={board}
              onChange={(e) => {
                setBoard(e.target.value);
                setChapter("");
                clearError("board");
              }}
            >
              <option value="">Select Board</option>
              {boards.map((b) => (
                <option key={b.id} value={b.id}>
                  {labelOf(b, "board")}
                </option>
              ))}
            </select>
            {errors.board && <p className="error-text">{errors.board}</p>}
          </div>

          {/* Grade */}
          <div className="dropdown-group">
            <label>Grade <span style={{ color: "red" }}>*</span></label>
            <select
              value={grade}
              onChange={(e) => {
                setStandard(e.target.value);
                setChapter("");
                clearError("grade");
              }}
            >
              <option value="">Select Grade</option>
              {grades.map((g) => (
                <option key={g.id} value={g.id}>
                  {labelOf(g, "grade")}
                </option>
              ))}
            </select>
            {errors.grade && <p className="error-text">{errors.grade}</p>}
          </div>

          {/* Subject */}
          <div className="dropdown-group">
            <label>Subject <span style={{ color: "red" }}>*</span></label>
            <select
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value);
                setChapter("");
                clearError("subject");
              }}
            >
              <option value="">Select Subject</option>
              {subjects.map((s) => (
                <option key={s.id} value={s.id}>
                  {labelOf(s, "subject")}
                </option>
              ))}
            </select>
            {errors.subject && <p className="error-text">{errors.subject}</p>}
          </div>

          {/* Chapter */}
          <div className="dropdown-group">
            <label>Chapter <span style={{ color: "red" }}>*</span></label>
            <select
              value={chapter}
              onChange={(e) => {
                setChapter(e.target.value);
                clearError("chapter");
              }}
              disabled={!chapters.length}
            >
              <option value="">{chapters.length ? "Select Chapter" : "Select board, grade & subject first"}</option>
              {chapters.map((c) => (
                <option key={c.id} value={c.id}>
                  {labelOf(c, "chapter")}
                </option>
              ))}
            </select>
            {errors.chapter && <p className="error-text">{errors.chapter}</p>}
          </div>

          {/* Difficulty */}
          <div className="dropdown-group">
            <label>Difficulty Level <span style={{ color: "red" }}>*</span></label>
            <select
              value={difficultylevel}
              onChange={(e) => {
                setDifficultylevel(e.target.value);
                clearError("difficultylevel");
              }}
            >
              <option value="">Select Difficulty</option>
              {difficulties.length ? (
                difficulties.map((d) => (
                  <option key={d.id} value={d.name || d.level || d.difficulty}>
                    {d.name || d.level || d.difficulty}
                  </option>
                ))
              ) : (
                <option value="" disabled>Loading difficulties...</option>
              )}
            </select>
            {errors.difficultylevel && <p className="error-text">{errors.difficultylevel}</p>}
          </div>
        </div>

        {/* Question Type */}
        <div className="question-type-row">
          <label><strong>Question Type: <span style={{ color: "red" }}>*</span></strong></label>
          <div className="q-label">
            {questionTypes.map(({ label, value }) => (
              <label key={value} style={{ marginRight: 15 }}>
                <input
                  type="radio"
                  name="questionType"
                  value={value}
                  className="crequestiontype"
                  checked={questionType === value}
                  onChange={() => {
                    setQuestionType(value);
                    setAnswerText("");
                    setOptions(["", "", "", ""]);
                    setCorrectAnswer(null);
                    clearError("questionType");
                  }}
                />{" "}
                {label}
              </label>
            ))}
          </div>
          {errors.questionType && <p className="error-text">{errors.questionType}</p>}
        </div>

        {/* Question Title (not for Matching) */}
        {questionType !== "Matching Types" && (
          <div className="question-title">
            <label><strong>Question Title: <span style={{ color: "red" }}>*</span></strong></label>
            <div className="editor-wrapper">
              <TiptapEditor
                content={questionTitle}
                onChange={(val) => {
                  setQuestionTitle(val);
                  clearError("questionTitle");
                }}
                small
              />
            </div>
            {errors.questionTitle && <p className="error-text">{errors.questionTitle}</p>}
          </div>
        )}

        {/* MCQ */}
        {questionType === "M.C.Q" && (
          <>
            <div className="options-row">
              <label><strong>Options: <span style={{ color: "red" }}>*</span></strong></label>
              <div className="mcq-options-grid">
                {options.map((option, index) => (
                  <div key={index} className="option-column">
                    <div className="editor-wrapper">
                      <TiptapEditor
                        content={option}
                        onChange={(val) => handleOptionChange(val, index)}
                      />
                    </div>
                  </div>
                ))}
              </div>
              {errors.options && <p className="error-text">{errors.options}</p>}
            </div>

            <div className="answer-selection">
              <label><strong>Select Correct Answer: <span style={{ color: "red" }}>*</span></strong></label>
              <div className="answer-radio-row">
                {options.map((_, index) => (
                  <label key={index} style={{ marginRight: 20 }}>
                    <input
                      type="radio"
                      name="correctAnswer"
                      checked={correctAnswer === index}
                      onChange={() => {
                        setCorrectAnswer(index);
                        clearError("correctAnswer");
                      }}
                    />{" "}
                    Option {index + 1}
                  </label>
                ))}
              </div>
              {errors.correctAnswer && <p className="error-text">{errors.correctAnswer}</p>}
            </div>
          </>
        )}

        {/* True / False */}
        {questionType === "True-False" && (
          <div className="answer-selection">
            <label><strong>Answer: <span style={{ color: "red" }}>*</span></strong></label>
            <div className="answer-radio-row">
              {["True", "False"].map((opt) => (
                <label key={opt} style={{ marginRight: 20 }}>
                  <input
                    type="radio"
                    name="tfAnswer"
                    value={opt}
                    checked={answer === opt}
                    onChange={(e) => {
                      setAnswerText(e.target.value);
                      clearError("answer");
                    }}
                  />{" "}
                  {opt}
                </label>
              ))}
            </div>
            {errors.answer && <p className="error-text">{errors.answer}</p>}
          </div>
        )}

        {/* Fill / Short / Long / Diagram */}
        {["Fill in the blanks", , "Very Short Question Answer", "Short Question Answer", "Long Question Answer", "Diagram-Based_Image-Based Questions"].includes(questionType) && (
          <div className="answer-editor">
            <label><strong>Answer: <span style={{ color: "red" }}>*</span></strong></label>
            <div className="editor-wrapper">
              <TiptapEditor
                content={answer}
                onChange={(val) => {
                  setAnswerText(val);
                  clearError("answer");
                }}
              />
            </div>
            {errors.answer && <p className="error-text">{errors.answer}</p>}
          </div>
        )}

        {/* Matching */}
        {questionType === "Matching Types" && (
          <>
            <div className="question-title">
              <label><strong>Question Title: <span style={{ color: "red" }}>*</span></strong></label>
              <div className="editor-wrapper">
                <TiptapEditor
                  content={questionTitle}
                  onChange={(val) => {
                    setQuestionTitle(val);
                    clearError("questionTitle");
                  }}
                  small
                />
              </div>
              {errors.questionTitle && <p className="error-text">{errors.questionTitle}</p>}
            </div>

            <div className="matching-grid">
              <div className="matching-column">
                <label><strong>Left Column: <span style={{ color: "red" }}>*</span></strong></label>
                <TiptapEditor
                  content={options[0]}
                  onChange={(val) => handleOptionChange(val, 0)}
                />
              </div>
              <div className="matching-column">
                <label><strong>Right Column: <span style={{ color: "red" }}>*</span></strong></label>
                <TiptapEditor
                  content={options[1]}
                  onChange={(val) => handleOptionChange(val, 1)}
                />
              </div>
            </div>
            {errors.options && <p className="error-text">{errors.options}</p>}

            <div className="answer-editor">
              <label><strong>Answer: <span style={{ color: "red" }}>*</span></strong></label>
              <div className="editor-wrapper">
                <TiptapEditor
                  content={answer}
                  onChange={(val) => {
                    setAnswerText(val);
                    clearError("answer");
                  }}
                />
              </div>
              {errors.answer && <p className="error-text">{errors.answer}</p>}
            </div>
          </>
        )}

        {/* Save */}
        <div className="save-button">
          <button className="save-btn" onClick={handleSave}>Save</button>
        </div>
      </main>
   {/* Profile Edit Modal */}
            {isModalOpen && (
                <div style={modalStyles.overlay}>
                    <div style={modalStyles.modal}>
                        <button style={modalStyles.closeBtn} onClick={() => setIsModalOpen(false)}>&times;</button>
                        <div style={modalStyles.userIcon}><FaUserCircle /></div>

                        <div style={modalStyles.formGroup}>
                            <label>Username</label>
                            <input
                                type="text"
                                value={user.name}
                                onChange={(e) => setUser({ ...user, name: e.target.value })}
                                style={modalStyles.input}
                            />
                        </div>

                        <div style={modalStyles.formGroup}>
                            <label>Email</label>
                            <input type="email" value={user.email} disabled style={{...modalStyles.input, background: '#f0f0f0', color:'#888'}} />
                        </div>

                        <div style={modalStyles.formGroup}>
                            <label>Password</label>
                            <input
                                type="password"
                                value={user.password}
                                onChange={(e) => setUser({ ...user, password: e.target.value })}
                                style={modalStyles.input}
                            />
                        </div>

                        <div style={{textAlign:'right'}}>
                            <button style={modalStyles.saveBtn} onClick={handleSaveProfile}>Save Changes</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Modal inline styles
const modalStyles = {
    overlay: {
        position: "fixed",
        top:0, left:0, width:"100%", height:"100%",
        background:"rgba(0,0,0,0.6)",
        display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000
    },
    modal: {
        background:"#fff", padding:"2rem", borderRadius:"12px",
        width:"90%", maxWidth:"400px", position:"relative",
        boxShadow:"0 4px 15px rgba(0,0,0,0.2)"
    },
    closeBtn: {
        position:"absolute", top:"10px", right:"15px",
        background:"none", border:"none", fontSize:"1.5rem", cursor:"pointer"
    },
    userIcon: {
        textAlign:"center", fontSize:"4rem", color:"#129255", marginBottom:"1rem"
    },
    formGroup: { marginBottom:"1rem" },
    input: { width:"100%", padding:"0.5rem 0.8rem", borderRadius:"6px", border:"1px solid #ccc", outline:"none" },
    saveBtn: { background:"#129255", color:"#fff", border:"none", padding:"0.6rem 1.2rem", borderRadius:"6px", cursor:"pointer", fontWeight:"bold" }
};

export default Createque;
